<template>
  <div class="admin-container">
    <!-- Sidebar - giữ nguyên như các trang khác -->
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <div class="section-header">
          <h2>Quản Lý Phương Tiện</h2>
          <div class="header-actions">
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="searchQuery"
                placeholder="Tìm kiếm theo tên lái xe, biển số..."
                @keyup.enter="handleSearch"
              />
              <button class="search-btn" @click="handleSearch">
                <i class="fas fa-search"></i>
              </button>
            </div>
            <button class="add-btn" @click="openAddModal">
              <i class="fas fa-plus"></i> Thêm mới
            </button>
          </div>
        </div>

        <div class="table-container">
          <!-- Loading State -->
          <div v-if="isLoading" class="table-loading">
            <div class="table-spinner"></div>
            <p>Đang tải dữ liệu...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="vehicles.length === 0" class="table-empty">
            <i class="fas fa-car table-empty-icon"></i>
            <h3 class="table-empty-title">Không tìm thấy phương tiện nào</h3>
            <p class="table-empty-text">Chưa có phương tiện nào được thêm vào hệ thống</p>
            <button class="table-empty-action" @click="openAddModal">
              <i class="fas fa-plus"></i> Thêm phương tiện
            </button>
          </div>

          <!-- Table Content -->
          <div v-else class="table-responsive has-mobile-cards">
            <!-- Desktop Table -->
            <table class="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Loại xe</th>
                  <th>Tài xế</th>
                  <th>Số điện thoại</th>
                  <th>Biển số</th>
                  <th class="col-actions">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="vehicle in vehicles"
                  :key="vehicle.vehicle_id"
                  :data-type="vehicle.type"
                >
                  <td class="nowrap">#{{ vehicle.vehicle_id }}</td>
                  <td>
                    <span class="vehicle-type-badge" :class="vehicle.type">
                      {{ formatVehicleType(vehicle.type) }}
                    </span>
                  </td>
                  <td>{{ vehicle.driver_name || 'N/A' }}</td>
                  <td class="nowrap">{{ vehicle.driver_phone || 'N/A' }}</td>
                  <td class="nowrap">
                    <span class="license-plate">{{ vehicle.license_plate || 'N/A' }}</span>
                  </td>
                  <td class="col-actions">
                    <div class="action-buttons">
                      <button
                        @click="openEditModal(vehicle)"
                        class="action-btn edit-btn"
                        title="Chỉnh sửa"
                        aria-label="Chỉnh sửa phương tiện"
                      >
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        @click="confirmDelete(vehicle)"
                        class="action-btn delete-btn"
                        title="Xóa"
                        aria-label="Xóa phương tiện"
                      >
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Mobile Card View -->
            <div class="mobile-card-list">
              <div v-for="vehicle in vehicles" :key="'card-' + vehicle.vehicle_id" class="mobile-card">
                <div class="mobile-card-header">
                  <div class="mobile-card-title">
                    <span class="mobile-card-id">#{{ vehicle.vehicle_id }}</span>
                    {{ formatVehicleType(vehicle.type) }}
                  </div>
                </div>
                <div class="mobile-card-body">
                  <div class="mobile-card-row">
                    <span class="mobile-card-label">Tài xế:</span>
                    <span class="mobile-card-value">{{ vehicle.driver_name || 'N/A' }}</span>
                  </div>
                  <div class="mobile-card-row">
                    <span class="mobile-card-label">Số điện thoại:</span>
                    <span class="mobile-card-value">{{ vehicle.driver_phone || 'N/A' }}</span>
                  </div>
                  <div class="mobile-card-row">
                    <span class="mobile-card-label">Biển số:</span>
                    <span class="mobile-card-value">
                      <span class="license-plate">{{ vehicle.license_plate || 'N/A' }}</span>
                    </span>
                  </div>
                </div>
                <div class="mobile-card-actions">
                  <button
                    class="action-btn edit-btn with-label"
                    @click="openEditModal(vehicle)"
                  >
                    <i class="fas fa-edit"></i> Sửa
                  </button>
                  <button
                    class="action-btn delete-btn with-label"
                    @click="confirmDelete(vehicle)"
                  >
                    <i class="fas fa-trash-alt"></i> Xóa
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-container" v-if="!isLoading && vehicles.length > 0">
          <div class="pagination-info">
            Hiển thị {{ vehicles.length }} phương tiện
          </div>
          <div class="pagination-controls">
            <button
              class="pagination-btn"
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
            >
              <i class="fas fa-chevron-left"></i> Trước
            </button>

            <span class="page-info">
              Trang {{ currentPage }} /
              {{ Math.ceil(totalItems / itemsPerPage) || 1 }}
            </span>

            <button
              class="pagination-btn"
              @click="changePage(currentPage + 1)"
              :disabled="currentPage >= Math.ceil(totalItems / itemsPerPage)"
            >
              Sau <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Vehicle Modal -->
    <div class="modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>
            {{ isEditing ? 'Chỉnh sửa phương tiện' : 'Thêm phương tiện mới' }}
          </h2>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitVehicleForm">
            <div class="form-group">
              <label>Loại xe:</label>
              <select v-model="vehicleForm.type" required>
                <option value="16_slots">Xe 16 chỗ</option>
                <option value="28_slots">Xe 28 chỗ</option>
                <option value="45_slots">Xe 45 chỗ</option>
                <option value="over_night">Xe giường nằm</option>
              </select>
            </div>
            <div class="form-group">
              <label>Tên tài xế:</label>
              <input
                type="text"
                v-model="vehicleForm.driver_name"
                placeholder="Nhập tên tài xế"
              />
            </div>
            <div class="form-group">
              <label>Số điện thoại:</label>
              <input
                type="text"
                v-model="vehicleForm.driver_phone"
                placeholder="Nhập số điện thoại"
                pattern="[0-9]{9,11}"
              />
            </div>
            <div class="form-group">
              <label>Biển số xe:</label>
              <input
                type="text"
                v-model="vehicleForm.license_plate"
                placeholder="Nhập biển số xe"
              />
            </div>

            <div class="modal-footer">
              <button type="button" class="cancel-btn" @click="closeModal">
                Hủy
              </button>
              <button type="submit" class="save-btn">
                {{ isEditing ? 'Cập nhật' : 'Thêm mới' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div
      class="modal"
      v-if="showDeleteConfirm"
      @click.self="showDeleteConfirm = false"
    >
      <div class="modal-content confirm-modal">
        <div class="modal-header">
          <h2>Xác nhận xóa</h2>
          <button class="close-btn" @click="showDeleteConfirm = false">
            ×
          </button>
        </div>
        <div class="modal-body">
          <p>
            Bạn có chắc chắn muốn xóa phương tiện có biển số
            <strong>"{{ vehicleToDelete?.license_plate }}"</strong>?
          </p>
          <p class="warning">
            Lưu ý: Hành động này không thể hoàn tác và sẽ xóa tất cả chuyến đi
            liên quan.
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="cancel-btn"
            @click="showDeleteConfirm = false"
          >
            Hủy
          </button>
          <button type="button" class="delete-btn" @click="deleteVehicle">
            Xóa
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import { ToastService } from '@/services/ToastService';


const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminVehicles',
  components: {
    AdminSidebar
  },
  data () {
    return {
      vehicles: [],
      isSidebarOpen: false,
      searchQuery: '',
      showModal: false,
      showDeleteConfirm: false,
      isEditing: false,
      vehicleToDelete: null,
      vehicleForm: {
        type: '28_slots',
        driver_name: '',
        driver_phone: '',
        license_plate: ''
      },
      currentUser: {
        full_name: 'Admin User',
        role: 'Administrator'
      },
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      isLoading: false
    }
  },
  computed: {
    userInitials () {
      return this.currentUser.full_name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    }
  },
  methods: {
    async fetchVehicles () {
      try {
        this.isLoading = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(`${API_BASE_URL}/admin/vehicles/`, {
          headers,
          params: {
            search: this.searchQuery || undefined,
            page: this.currentPage,
            per_page: this.itemsPerPage
          }
        })

        if (response.data.items) {
          this.vehicles = response.data.items
          this.totalItems = response.data.total
        } else {
          this.vehicles = response.data
          this.totalItems = response.data.length
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error)
        if (error.response?.status === 401) {
          this.handleLogout()
        }
      } finally {
        this.isLoading = false
      }
    },
    handleLogout () {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('refreshToken')
      this.$router.push('/login')
    },
    formatVehicleType (type) {
      const types = {
        '16_slots': 'Xe 16 chỗ',
        '28_slots': 'Xe 28 chỗ',
        '45_slots': 'Xe 45 chỗ',
        over_night: 'Xe giường nằm'
      }
      return types[type] || type
    },
    handleSearch () {
      this.currentPage = 1
      this.fetchVehicles()
    },
    changePage (newPage) {
      this.currentPage = newPage
      this.fetchVehicles()
    },
    openAddModal () {
      this.isEditing = false
      this.vehicleForm = {
        type: '28_slots',
        driver_name: '',
        driver_phone: '',
        license_plate: ''
      }
      this.showModal = true
    },
    openEditModal (vehicle) {
      this.isEditing = true
      this.vehicleForm = { ...vehicle }
      this.showModal = true
    },
    closeModal () {
      this.showModal = false
    },
    confirmDelete (vehicle) {
      this.vehicleToDelete = vehicle
      this.showDeleteConfirm = true
    },
    async submitVehicleForm () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        if (this.isEditing) {
          await axiosInstance.put(
            `${API_BASE_URL}/admin/vehicles/${this.vehicleForm.vehicle_id}`,
            this.vehicleForm,
            { headers }
          )
        } else {
          await axiosInstance.post(
            `${API_BASE_URL}/admin/vehicles/`,
            this.vehicleForm,
            {
              headers
            }
          )
        }

        this.fetchVehicles()
        this.closeModal()

        ToastService.success(
          `Phương tiện đã được ${
            this.isEditing ? 'cập nhật' : 'thêm mới'
          } thành công.`
        )
      } catch (error) {
        console.error('Error saving vehicle:', error)
        if (error.response?.status === 409) {
          ToastService.error('Biển số xe này đã tồn tại trong hệ thống.')
        } else {
          ToastService.error('Có lỗi xảy ra khi lưu dữ liệu. Vui lòng thử lại sau.')
        }
      }
    },
    async deleteVehicle () {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        await axiosInstance.delete(
          `${API_BASE_URL}/vehicles/${this.vehicleToDelete.vehicle_id}`,
          { headers }
        )

        this.vehicles = this.vehicles.filter(
          vehicle => vehicle.vehicle_id !== this.vehicleToDelete.vehicle_id
        )
        this.showDeleteConfirm = false
        this.vehicleToDelete = null
      } catch (error) {
        console.error('Error deleting vehicle:', error)
        if (error.response?.status === 409) {
          ToastService.error('Không thể xóa phương tiện này vì đã có chuyến đi liên quan.')
        } else {
          ToastService.error('Có lỗi xảy ra khi xóa dữ liệu. Vui lòng thử lại sau.')
        }
      }
    },
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    }
  },
  mounted () {
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) this.isSidebarOpen = sidebarState === 'true'
    this.fetchVehicles()
  }
}
</script>

<style scoped>
@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';

/* Thêm style đặc thù cho trang này */
select {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
}
.admin-content {
  padding: 24px;
  background-color: #f9fafb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Cải thiện header phần */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.section-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header h2:before {
  content: '\f1b9'; /* Car icon */
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  color: #4b5563;
  font-size: 1.2rem;
}

/* Cải thiện phần tìm kiếm */
.header-actions {
  display: flex;
  gap: 16px;
  align-items: center;
}

.search-box {
  position: relative;
  flex-grow: 1;
}

.search-box input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s;
  background-color: white;
}

.search-box input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-box i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}

.search-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: #f3f4f6;
  border: none;
  height: 32px;
  width: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.search-btn:hover {
  background: #e5e7eb;
}

.search-btn i {
  position: static;
  transform: none;
  color: #4b5563;
}

/* Nút thêm mới */
.add-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.add-btn:hover {
  background-color: #2563eb;
}

/* Cải thiện bảng */
.table-container {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

th {
  background-color: #f9fafb;
  padding: 14px 20px;
  text-align: left;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
}

td {
  padding: 14px 20px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

tbody tr:hover {
  background-color: #f9fafb;
}

tbody tr:last-child td {
  border-bottom: none;
}

/* Thêm badge hiển thị loại xe */
td:nth-child(2) {
  font-weight: 500;
}

tr td:nth-child(2)::before {
  content: '';
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

/* Màu sắc cho từng loại xe */
tr[data-type='16_slots'] td:nth-child(2)::before {
  background-color: #4b5563;
}

tr[data-type='28_slots'] td:nth-child(2)::before {
  background-color: #3b82f6;
}

tr[data-type='45_slots'] td:nth-child(2)::before {
  background-color: #10b981;
}

tr[data-type='over_night'] td:nth-child(2)::before {
  background-color: #f59e0b;
}

/* Cải thiện các nút thao tác */
.action-btn {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit {
  background-color: #eff6ff;
  color: #3b82f6;
}

.edit:hover {
  background-color: #dbeafe;
}

.delete {
  background-color: #fef2f2;
  color: #ef4444;
}

.delete:hover {
  background-color: #fee2e2;
}

/* Cải thiện phân trang */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.pagination-btn {
  padding: 8px 16px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #4b5563;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: #4b5563;
}

/* Modern Pulse Dots Loading Spinner */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
}

.loading-container p {
  color: #64748b;
  font-size: 0.95rem;
  margin: 0;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  padding: 0;
}

.spinner {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner::before,
.spinner::after,
.spinner span {
  content: '';
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #283e37 0%, #48bb78 100%);
  animation: pulse-bounce 1.4s ease-in-out infinite;
}

.spinner::before {
  animation-delay: 0s;
}

.spinner span {
  animation-delay: 0.2s;
}

.spinner::after {
  animation-delay: 0.4s;
}

@keyframes pulse-bounce {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Cải thiện trạng thái trống */
.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #6b7280;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 16px;
  color: #9ca3af;
}

.empty-state p {
  font-size: 1.1rem;
  margin-bottom: 24px;
}

/* Cải thiện modal */
.modal {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modal-content {
  max-width: 500px;
  width: 90%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: modal-slide-down 0.3s ease-out;
}

@keyframes modal-slide-down {
  from {
    transform: translateY(-30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  background: linear-gradient(to right, #3b82f6, #60a5fa);
  padding: 20px;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  background-color: white;
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #4b5563;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group select {
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="%236B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
}

.modal-footer {
  padding: 16px 24px;
  background-color: #f9fafb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn,
.save-btn,
.delete-btn {
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn {
  background-color: white;
  color: #4b5563;
  border: 1px solid #e5e7eb;
}

.cancel-btn:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.save-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
}

.save-btn:hover {
  background-color: #2563eb;
}

.delete-btn {
  background-color: #ef4444;
  color: white;
  border: none;
}

.delete-btn:hover {
  background-color: #dc2626;
}

/* Xác nhận xóa */
.confirm-modal {
  max-width: 450px;
}

.warning {
  color: #b91c1c;
  background-color: #fee2e2;
  border-radius: 6px;
  padding: 12px;
  margin-top: 16px;
  border-left: 4px solid #ef4444;
  font-size: 0.95rem;
}

/* Responsive */
@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .table-container {
    overflow-x: auto;
  }

  table {
    min-width: 700px;
  }
}
</style>
